import { useState } from "react";
import { Flag, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { SEVERITY_HINT, fmtClock } from "../../lib/support";

const CATEGORIES = [
  { key: "subtitle_translation", label: "Subtitle / Translation" },
  { key: "transcription", label: "Transcription" },
  { key: "subtitle_timing", label: "Subtitle Timing" },
];

const SEVERITIES = [
  { key: "minor", label: "Minor" },
  { key: "incorrect", label: "Incorrect" },
  { key: "major", label: "Major" },
  { key: "critical", label: "Critical" },
];

// Reports one already-stored subtitle line. Nothing is regenerated, re-timed
// or re-segmented — the cue is looked up server-side from the saved result.
export const ReportCueDialog = ({ open, onOpenChange, jobId, cue }) => {
  const [category, setCategory] = useState("subtitle_translation");
  const [severity, setSeverity] = useState("incorrect");
  const [description, setDescription] = useState("");
  const [expected, setExpected] = useState("");
  const [busy, setBusy] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const reset = () => {
    setDescription(""); setExpected(""); setTicketId(null);
    setCategory("subtitle_translation"); setSeverity("incorrect");
  };

  const submit = async () => {
    if (description.trim().length < 10) {
      return toast.error("Tell us what's wrong with this line (10+ characters)");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/support/tickets", {
        category,
        severity,
        description: description.trim(),
        expected_result: expected.trim() || null,
        job_id: jobId,
        cue: { cue_index: cue?.index },
        attachment_ids: [],
      });
      setTicketId(data.ticket_id);
      toast.success(`Reported · ${data.ticket_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not report this line");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}
    >
      <DialogContent
        data-testid="report-cue-dialog"
        className="bg-zinc-950 border-zinc-800 text-white rounded-none max-w-xl"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <Flag className="w-4 h-4 text-red-400" /> Report a problem with this subtitle
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            We attach this exact line, its timestamp and the video — your
            subtitles are not changed.
          </DialogDescription>
        </DialogHeader>

        {ticketId ? (
          <div className="py-4 space-y-4" data-testid="report-cue-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-white font-semibold">
                  Your support request has been received.
                </div>
                <div className="text-xs text-zinc-400 mt-1">
                  Ticket <span className="font-mono text-zinc-200">{ticketId}</span> ·
                  track it under Help &amp; Support → My Tickets.
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => { reset(); onOpenChange(false); }}
              data-testid="report-cue-close-btn"
              className="text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:bg-zinc-900 text-zinc-300 px-4 py-2 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="space-y-5 py-2">
            <div className="border border-zinc-800 bg-zinc-900/40 p-4 space-y-1.5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                Line #{String(cue?.index ?? 0).padStart(2, "0")} ·{" "}
                {fmtClock(cue?.start)} → {fmtClock(cue?.end)}
              </div>
              <div className="text-xs font-mono text-zinc-500">{cue?.source_text}</div>
              <div className="text-sm text-white">{cue?.translated_text}</div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
                  Category
                </label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger
                    data-testid="report-cue-category"
                    className="bg-zinc-900 border-zinc-800 rounded-none h-10 text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
                  Severity
                </label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger
                    data-testid="report-cue-severity"
                    className="bg-zinc-900 border-zinc-800 rounded-none h-10 text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
                    {SEVERITIES.map((s) => (
                      <SelectItem key={s.key} value={s.key}>
                        {s.label} · {SEVERITY_HINT[s.key]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
                What's wrong with it?
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={5000}
                data-testid="report-cue-description"
                placeholder="e.g. the name was translated instead of kept as-is"
                className="bg-zinc-900 border-zinc-800 rounded-none text-white placeholder:text-zinc-600 resize-none"
              />
            </div>

            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
                What should it say? <span className="text-zinc-600">(optional)</span>
              </label>
              <Textarea
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                rows={2}
                maxLength={2000}
                data-testid="report-cue-expected"
                className="bg-zinc-900 border-zinc-800 rounded-none text-white placeholder:text-zinc-600 resize-none"
              />
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="report-cue-submit"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-5 py-2.5 transition-colors"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Send report
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
